import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
const Leads = React.lazy(() => import("./pages/Leads"));
const LeadDetail = React.lazy(() => import("./pages/LeadDetail"));
const FollowUps = React.lazy(() => import("./pages/FollowUps"));
const RoleTargetsSettings = React.lazy(() => import("./pages/RoleTargetsSettings"));
const ComingSoon = React.lazy(() => import("./pages/ComingSoon"));
const Projects = React.lazy(() => import("./pages/Projects"));
const SiteVisits = React.lazy(() => import("./pages/SiteVisits"));
const LeadPool = React.lazy(() => import("./pages/LeadPool"));
const StarredLeads = React.lazy(() => import("./pages/StarredLeads"));
const TeamPerformance = React.lazy(() => import("./pages/TeamPerformance"));
const TeamMemberDetail = React.lazy(() => import("./pages/TeamMemberDetail"));
const TeamAttendance = React.lazy(() => import("./pages/TeamAttendance"));
const ActivityLog = React.lazy(() => import("./pages/ActivityLog"));
const RequestForData = React.lazy(() => import("./pages/RequestForData"));
const MyAttendance = React.lazy(() => import("./pages/MyAttendance"));
const Broadcasts = React.lazy(() => import("./pages/Broadcasts"));
const AddLead = React.lazy(() => import("./pages/AddLead"));
const DayEndReport = React.lazy(() => import("./pages/DayEndReport"));
const LoginSessions = React.lazy(() => import("./pages/LoginSessions"));
const AccessControl = React.lazy(() => import("./pages/AccessControl"));
const LeadsManagement = React.lazy(() => import("./pages/LeadsManagement"));
const DummyLeads = React.lazy(() => import("./pages/DummyLeads"));
const PasswordResets = React.lazy(() => import("./pages/PasswordResets"));
const Settings = React.lazy(() => import("./pages/Settings"));
const SignupRequests = React.lazy(() => import("./pages/SignupRequests"));
const StatusUpdates = React.lazy(() => import("./pages/StatusUpdates"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const VerifyEmail = React.lazy(() => import("./pages/VerifyEmail"));
const SharedWithMe = React.lazy(() => import("./pages/SharedWithMe"));
const SharedByMe = React.lazy(() => import("./pages/SharedByMe"));
import { RoleGuard } from "./components/RoleGuard";
const CRMAccess = React.lazy(() => import("./pages/CRMAccess"));
const SourceCodes = React.lazy(() => import("./pages/SourceCodes"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <React.Suspense fallback={<div className="p-8 text-sm text-muted-foreground" data-testid="page-loading">Loading…</div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<RoleGuard perm="dashboard"><Dashboard /></RoleGuard>} />
              <Route path="leads" element={<RoleGuard perm="leads"><Leads /></RoleGuard>} />
              <Route path="leads/starred" element={<RoleGuard perm="starred"><StarredLeads /></RoleGuard>} />
              <Route path="leads/:id" element={<RoleGuard perm="leads"><LeadDetail /></RoleGuard>} />
              <Route path="followups" element={<RoleGuard perm="leads"><FollowUps /></RoleGuard>} />
              <Route path="projects" element={<RoleGuard perm="projects"><Projects /></RoleGuard>} />
              <Route path="site-visits" element={<RoleGuard perm="site_visits"><SiteVisits /></RoleGuard>} />
              <Route path="requests" element={<RoleGuard perm="requests"><RequestForData /></RoleGuard>} />
              <Route path="attendance" element={<RoleGuard perm="attendance"><MyAttendance /></RoleGuard>} />
              <Route path="broadcasts" element={<RoleGuard perm="broadcasts" roles={["super_admin","admin"]}><Broadcasts /></RoleGuard>} />
              <Route path="add-lead" element={<AddLead />} />
              <Route path="lead-pool" element={<RoleGuard perm="lead_pool"><LeadPool /></RoleGuard>} />
              <Route path="shared-with-me" element={<RoleGuard perm="shares"><SharedWithMe /></RoleGuard>} />
              <Route path="shared-by-me" element={<RoleGuard perm="shares"><SharedByMe /></RoleGuard>} />
              <Route path="day-end-report" element={<RoleGuard perm="day_end_report"><DayEndReport /></RoleGuard>} />
              <Route path="settings" element={<Settings />} />
              <Route path="login-sessions" element={<RoleGuard perm="login_sessions" roles={["super_admin","admin"]}><LoginSessions /></RoleGuard>} />
              <Route path="status-updates" element={<RoleGuard perm="status_updates" roles={["super_admin"]}><StatusUpdates /></RoleGuard>} />
              <Route path="access" element={<RoleGuard perm="access_control" roles={["super_admin","admin"]}><AccessControl /></RoleGuard>} />
              <Route path="leads-management" element={<RoleGuard perm="approval_inbox" roles={["super_admin"]}><LeadsManagement /></RoleGuard>} />
              <Route path="dummy-leads" element={<RoleGuard perm="dummy" roles={["super_admin"]}><DummyLeads /></RoleGuard>} />
              <Route path="signup-requests" element={<RoleGuard perm="signup_requests" roles={["super_admin","admin"]}><SignupRequests /></RoleGuard>} />
              <Route path="password-resets" element={<RoleGuard perm="password_resets" roles={["super_admin","admin"]}><PasswordResets /></RoleGuard>} />
              <Route path="settings/role-targets" element={<RoleGuard perm="role_targets" roles={["super_admin"]}><RoleTargetsSettings /></RoleGuard>} />
              <Route path="team-performance" element={<RoleGuard perm="team_performance" roles={["super_admin","admin","team_leader"]}><TeamPerformance /></RoleGuard>} />
              <Route path="team-performance/:id" element={<RoleGuard perm="team_performance" roles={["super_admin","admin","team_leader"]}><TeamMemberDetail /></RoleGuard>} />
              <Route path="team-attendance" element={<RoleGuard perm="team_attendance" roles={["super_admin","admin","hr"]}><TeamAttendance /></RoleGuard>} />
              <Route path="activity" element={<RoleGuard perm="activity_log" roles={["super_admin","admin","team_leader","hr"]}><ActivityLog /></RoleGuard>} />
              <Route path="crm-access" element={<RoleGuard roles={["super_admin"]}><CRMAccess /></RoleGuard>} />
              <Route path="source-codes" element={<RoleGuard roles={["super_admin"]}><SourceCodes /></RoleGuard>} />
              <Route path="coming-soon" element={<ComingSoon />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </React.Suspense>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
